/**
 * ObjectPeek — THE way any object opens (Matt, 2026-07-20: "When I click on a
 * goal it should show the modal first, when I click on an issue, etc.").
 *
 * One kind-agnostic peek for every canonical object: goal, customer, lead,
 * opportunity, candidate, application, job, person, organization, project,
 * workflow, task. It reads `objects.get` — a thin projection of the SAME
 * `loadObject` the full page uses — so the modal and `/o/<uid>` can never
 * disagree about what a thing is. Expand routes to that page.
 *
 * Built on the PeekDialog "Split · Calibrated" grammar: masthead = caps kind
 * eyebrow + title; state lives in a labeled "Status" row — never a tag beside
 * the title.
 *
 * COLUMN ASSIGNMENT WAS INVERTED on 2026-07-21 (Matt: "on Job, Deal, etc. the
 * properties and timeline should be flipped — the properties are the main point
 * of that modal, not the timeline"). It now reads:
 *
 *   MAIN    outcome → **Description** → **Properties** → kind body → Related
 *   RAIL    **Activity**
 *   FOOTER  aggregate write status · the object's buttons (static, always there)
 *
 * That order is fixed on purpose (Matt, 2026-07-21: "it should always go Title /
 * Description / Properties"): the masthead says what this is, the description
 * says it in sentences, and everything after that is structured fact. Kind
 * extensions moved BELOW Properties so nothing a kind adds can come between the
 * three that are always the same. Both prose sections are edit-in-place wherever
 * the kind ships a descriptor for them.
 *
 * The narrative sections were already conditional, which is why ONE composition
 * serves both temperaments and there is no `if (kind)` here: a deal has no
 * outcome or description, so its main column IS its property list; a goal keeps
 * its outcome and description on top and its properties beneath them. The
 * previous arrangement put a 288px-wide property rail beside a main column that,
 * for every property-led kind, held nothing but an event log.
 *
 * "Details" (Opened/Updated) is deliberately gone — see `completedField`.
 *
 * THERE IS EXACTLY ONE PEEK (2026-07-21). `task` used to be the exception — a
 * separate 750-line `TaskPeek` reached through a whole-body override, with its
 * own title editor, its own description editor, its own property controls and
 * its own action wiring. Every design decision had to be made twice and the two
 * copies drifted (both independently grew the same `md:type-body` masthead bug;
 * only one ever got the properties-first arrangement). It is folded: an issue is
 * a `KIND_EDITORS` entry, and this component renders it.
 *
 * KIND EXTENSIONS. Kinds differ in what they let you DO, not in how they read,
 * so the chrome is shared and the differences arrive through `slots` — the same
 * dispatch shape `/o/[uid]` uses for `kindExtensions(kind, uid)`. A slot may ADD
 * a main section, ADD a sidebar group or ADD a footer button. It may not replace
 * the body and it may not replace the property rows: how an object reads is not
 * a per-kind decision, and the two seams that said otherwise were deleted with
 * the fork. Slots live in the shell because they reach for server actions; this
 * block stays framework-agnostic.
 */

import * as React from "react";
import { useSearch as useSearchParams, RouterLink as NextLink } from "../router";
import { useApi } from "../api/client";
import { objects, type ObjectPeekDto } from "../api/contracts/objects";
import { PEEK_PARAM, useCloseObject } from "./object-link";
import {
  ReadOnlyProse,
  isLockedProse,
  selectProseSlots,
} from "./object-peek-prose";
import { useObjectEdit } from "./object-edit-context";
import { Button } from "../button";
import { Skeleton } from "../skeleton";
import {
  PeekDialog,
  PeekDialogFooter,
  PeekDialogPropertyChips,
  PeekDialogSection,
  PeekDialogTimeline,
  type PeekField,
  type PeekTimelineEvent,
} from "./peek-dialog";
import { commitmentStateIntent } from "./commitment-table";
import { formatDateTime } from "../format";
import {
  Box,
  Briefcase,
  Building2,
  Calendar,
  CircleDot,
  DollarSign,
  Flag,
  Hash,
  Link2,
  Milestone,
  Percent,
  Tag,
  Target,
  UserRound,
  Users,
} from "../icons";
import { iconForKind, colorForKind, labelForKind } from "../tokens/object-identity";

/** How many history rows the peek shows before deferring to the full page. */
const TIMELINE_ROWS = 4;

/** "in_progress" → "In progress" — the shared token→label fold. */
const titleCase = (s: string) =>
  s.replace(/[_-]/g, " ").replace(/^./, (c) => c.toUpperCase());

/**
 * The kind's masthead badge — its canonical glyph on a section-tinted square
 * (Matt, 2026-07-21: "the iconography ... needs to be consistent throughout the
 * app and colored according to its section/type"). Icon AND color come from the
 * ONE identity map (`tokens/object-identity.ts`), so the peek can never disagree
 * with the dock, the create menu or a list badge about what a kind looks like,
 * and the color is always its section's accent from the single color source.
 *
 * 24px, down from 36px (Matt, 2026-07-22: "that icon box needs to be smaller").
 * It stopped being a standalone badge on its own line and became the leading
 * glyph of the kind bar, where it sits inside the 28px band the ghost controls
 * set — at 36px it was the tallest thing in the masthead and dictated the row's
 * height instead of belonging to it. The tint/rounding recipe is unchanged; only
 * the box and glyph scale down, and `rounded-md` keeps the corner radius
 * proportional to the smaller square.
 */
/**
 * The kind bar's TEXT — "Task", or "Apple → Lead" when the object is a child
 * (Matt, 2026-07-22: "if we are creating a task inside of a project or a lead
 * inside of an account ... we need it to be {object} -> Child at the title. The
 * apple in this example would need to be clickable to go to the account").
 *
 * The parent is a LINK and the kind is not, which is the whole point of the
 * pair: one half is somewhere you can go, the other names what you are looking
 * at. `stopPropagation` keeps a click on the parent from also reaching the
 * peek's own dismiss handlers — navigating away and closing are the same
 * intention here, but Next must own the order.
 *
 * Renders the bare noun whenever `parent` is absent, which is every object whose
 * kind has no container wired server-side (`loadParent`) — so a kind gains its
 * breadcrumb by growing a resolver clause, with no change here.
 */
function ObjectKindLine({ obj }: { obj: ObjectPeekDto }) {
  const kindLabel = labelForKind(obj.kind);
  const parent = obj.parent ?? null;
  if (!parent) return <>{kindLabel}</>;
  return (
    <>
      <NextLink
        href={parent.href}
        onClick={(e) => e.stopPropagation()}
        title={`Open ${parent.label}`}
        className="rounded-sm underline-offset-2 outline-none hover:text-foreground hover:underline focus-visible:text-foreground focus-visible:underline"
      >
        {parent.label}
      </NextLink>
      <span aria-hidden className="px-1 text-muted-foreground/60">
        →
      </span>
      {kindLabel}
    </>
  );
}

function MastheadIcon({ kind }: { kind: string }) {
  const Glyph = iconForKind(kind);
  const color = colorForKind(kind);
  return (
    // NO TOOLTIP, and for the same reason the kind bar exists at all: the noun
    // is ALREADY THERE, 8px to the right, in its own slot — pairing them was the
    // 2026-07-22 change ("we need the name of the object - like task"). A bubble
    // saying "Task" beside the word "Task" has nowhere honest to go either: it
    // sits on the panel's top-left corner, so upward it escapes the modal and
    // rightward it covers the very word it is repeating. See `peek-dialog.tsx`
    // for the rule — a glyph earns a hover when it STANDS IN FOR a word, not
    // when it merely accompanies one.
    <span
      className="flex size-6 items-center justify-center rounded-md"
      // A ~12% section tint behind the full-strength section-colored glyph.
      style={{ backgroundColor: `${color}1f`, color }}
    >
      <Glyph className="size-[13px]" />
    </span>
  );
}

/**
 * The leading glyph for a property CHIP, chosen from what the property IS. Label
 * first (an "Owner" is a person whatever its column type), type as the fallback
 * (a bare `date` field with an odd label still leads with a calendar). Returns
 * null for the lifecycle state — its own intent dot is already the chip's glyph,
 * and a second circle beside it would be the same mark twice.
 */
function propertyGlyph(label: string, type?: string): React.ReactNode {
  const l = label.trim().toLowerCase();
  const has = (...words: string[]) => words.some((w) => l.includes(w));

  if (type === "state" || has("status")) return null;
  if (has("owner", "lead", "assignee", "contact", "reviewer", "person", "manager"))
    return <UserRound />;
  if (has("organization", "company", "account", "employer")) return <Building2 />;
  if (has("team", "department", "group", "workspace")) return <Users />;
  if (has("priority", "severity", "urgency")) return <Flag />;
  if (has("stage", "lane", "pipeline", "step", "phase")) return <Milestone />;
  if (
    has("amount", "value", "salary", "revenue", "price", "budget", "cost", "mrr", "arr")
  )
    return <DollarSign />;
  if (has("probability", "confidence", "score", "percent", "rate")) return <Percent />;
  if (has("project")) return <Box />;
  if (has("goal", "objective")) return <Target />;
  if (has("job", "role")) return <Briefcase />;
  if (has("category", "type", "tier", "threat", "segment", "kind", "tag"))
    return <Tag />;
  if (has("due", "date", "target", "start", "close", "complete", "deadline", "when"))
    return <Calendar />;

  switch (type) {
    case "date":
      return <Calendar />;
    case "number":
      return <Hash />;
    case "reference":
      return <Link2 />;
    case "url":
      return <Link2 />;
    default:
      return <CircleDot />;
  }
}

/**
 * Kind-specific additions to the shared chrome. Every slot receives the loaded
 * object and a `refresh` that re-reads it AND revalidates the page behind, so a
 * mutation made in the peek is visible in both places immediately.
 */
export interface ObjectPeekSlots {
  /** Extra MAIN-column sections under the description. */
  bodyExtension?: (obj: ObjectPeekDto, refresh: () => void) => React.ReactNode;
  /** Extra SIDEBAR groups under Properties. */
  sidebarExtension?: (obj: ObjectPeekDto, refresh: () => void) => React.ReactNode;
  /** Extra buttons in the static footer, between the destructive control and Done. */
  footerExtras?: (obj: ObjectPeekDto, refresh: () => void) => React.ReactNode;
  /** Override the masthead eyebrow (default: the title-cased kind). */
  eyebrow?: (obj: ObjectPeekDto) => React.ReactNode;
  // NO `renderForKind` AND NO `propertyFields`. Both were escape hatches for a
  // kind that wanted a different composition, and both are gone with the reason
  // they existed. `renderForKind` had exactly one caller — `task`, handing the
  // whole body to a second 750-line peek — and that fork is now a `KIND_EDITORS`
  // entry rendered by this component. `propertyFields` shipped with a doc block
  // and zero callers, because the editable rows arrive through the edit context
  // instead.
  //
  // Leaving them would be leaving the door open to re-forking: the next kind
  // that felt special would take the whole-body branch, and the design notes
  // would have two places to land again. A kind extends this peek by ADDING
  // (a section, a rail group, a button) or it declares descriptors. There is no
  // third option, and that is the point.
}

export interface ObjectPeekProps extends ObjectPeekSlots {
  /** The object to peek (its ULID), or null when closed. */
  uid: string | null;
  onClose: () => void;
  /**
   * Origin mode carried into the Expand href (`?from=<mode>`) so the full page
   * keeps its bearings — the rail lights and the crumb resolve from it.
   */
  from?: string | null;
  /** Re-read the route behind the peek after a mutation (usually router.refresh). */
  onRefresh?: () => void;
  /**
   * The universal soft-delete seam (`archiveObjectAction`), threaded from the
   * shell. When present, a peek whose kind is archivable (`obj.remove`) gets the
   * destructive control in the footer's far-left slot — ONE implementation for
   * every kind, replacing the Archive button the task peek used to draw itself.
   * Absent ⇒ no destructive control, exactly as before.
   */
  onRemove?: (kind: string, uid: string) => Promise<unknown>;
}

export function ObjectPeek({
  uid,
  onClose,
  from,
  onRefresh,
  onRemove,
  bodyExtension,
  sidebarExtension,
  footerExtras,
  eyebrow,
}: ObjectPeekProps) {
  const { data: obj, error, mutate } = useApi(
    objects.get,
    uid ? { params: { uid } } : null,
  );

  const refresh = React.useCallback(() => {
    void mutate();
    onRefresh?.();
  }, [mutate, onRefresh]);

  // The shell's editable provider, when mounted. Null ⇒ the rail is read-only,
  // exactly as before — the fold below is purely additive.
  const edit = useObjectEdit();

  if (uid == null) return null;

  const expandHref = obj
    ? from
      ? `${obj.href}${obj.href.includes("?") ? "&" : "?"}from=${from}`
      : obj.href
    : undefined;

  const events: PeekTimelineEvent[] = (obj?.events ?? [])
    .slice(0, TIMELINE_ROWS)
    .map((e) => ({
      uid: e.uid,
      label: e.label,
      summary: e.summary,
      actorLabel: e.actorLabel,
      occurredAt: e.occurredAt,
      critical: e.critical,
    }));

  /**
   * THE PROSE DESCRIPTOR — the kind's long-form body text, pulled out of the
   * property list and given the Description section, edit-in-place (Matt,
   * 2026-07-21: "all descriptions should also be edit in place like the
   * titles").
   *
   * Identified by TYPE, not by key: `longtext` is the descriptor vocabulary's
   * own word for "this is prose", and keying off `f.key === "description"` would
   * be the kind-name sniffing this whole registry exists to delete — a kind that
   * calls its body `summary` or `brief` would silently keep rendering a textarea
   * inside a 96px-labeled row. The FIRST one wins; a kind with two prose fields
   * shows the second as a row, which is at least visible rather than dropped.
   *
   * A field flagged `outcome` is passed over: it is prose too, but it has its own
   * section below, and without this a goal's outcome (declared before its
   * description in some orderings) would take the description's slot and then
   * render a second time under "Outcome".
   *
   * A LOCKED prose field is passed over too, and that exclusion is load-bearing
   * (#43855) — the routing rule, and WHY it is the rule, lives in
   * `object-peek-prose.tsx` beside the pure function that applies it.
   */
  const slots = selectProseSlots(obj?.fields ?? []);

  // The OUTCOME slot is selected without the provider guard the body carries,
  // because it now renders a LOCKED outcome read-only — which needs no provider.
  // An unlocked outcome with no provider mounted still falls through to the
  // plain `targetOutcome` paragraph below, exactly as before.
  const outcomeField = obj ? slots.outcome : null;

  const proseField = obj && edit?.renderProse ? slots.body : null;

  /**
   * Read-only prose descriptors — one labelled section each (below), never a
   * property chip: a whole paragraph crammed into the inline chip row would be
   * unreadable, which is the same reason `outcome` is kept out of the chips.
   */
  const lockedProseFields = obj ? slots.readOnly : [];

  /**
   * The default read-only property rail: state in its labeled row (never a tag
   * in the title), the resolved owner, then the kind's vitals. Empty rows drop
   * themselves in `PeekDialogFields`.
   */
  const defaultProperties = (o: ObjectPeekDto): PeekField[] => [
    {
      label: "Status",
      badge: {
        label: titleCase(o.state),
        intent: commitmentStateIntent(o.state),
      },
    },
    { label: "Owner", icon: propertyGlyph("Owner"), value: o.owner?.name },
    ...o.vitals.map((v) => ({
      label: v.label,
      icon: propertyGlyph(v.label),
      value: v.value,
      hint: v.hint ?? undefined,
      intent: v.intent ?? undefined,
    })),
  ];

  /**
   * THE PROPERTY ROW — every property as ONE inline chip, editable in place
   * (Matt, 2026-07-21: "if we have a status like 'In Progress' inline with Alan
   * Turing, we can just let that turn into a dropdown — we don't need the
   * dropdowns below it with the same information"). The earlier split (read-only
   * chips + a duplicate editable list) is gone: a chip IS the editor. A status
   * chip opens its dropdown, a date chip its picker; the chip stays clean until
   * you reach for it (see the control's chip mode). Read-only facts (Owner when
   * it has no editor, vitals) sit inline beside them; an editable field the
   * loader also surfaced as a vital shows once (the vital is dropped).
   *
   * Falls back to the read-only chips when no provider is mounted.
   */
  const editableChips = (o: ObjectPeekDto): PeekField[] => {
    const fields = o.fields ?? [];
    if (!edit || fields.length === 0)
      return [...defaultProperties(o), ...completedField(o)];

    const covered = new Set(fields.map((f) => f.label.trim().toLowerCase()));
    const statusField = fields.find((f) => f.label.trim().toLowerCase() === "status");
    const hoistIdentity = !!edit.renderTitle;

    const rows: PeekField[] = [
      statusField
        ? {
            ...edit.renderField(statusField, { chip: true }),
            icon: propertyGlyph(statusField.label, statusField.type),
          }
        : {
            label: "Status",
            badge: {
              label: titleCase(o.state),
              intent: commitmentStateIntent(o.state),
            },
          },
    ];
    // The read-only owner chip only when NO editable field already IS the owner
    // — a task's Assignee, a lead's Contact and a goal's owner are the same
    // person the loader put in `o.owner`, so showing both painted "Alan Turing"
    // twice (once read, once editable).
    const ownerCovered = ["owner", "assignee", "lead", "contact", "reviewer"].some(
      (l) => covered.has(l),
    );
    if (o.owner?.name && !ownerCovered) {
      rows.push({ label: "Owner", icon: propertyGlyph("Owner"), value: o.owner.name });
    }
    for (const f of fields) {
      if (f === statusField) continue;
      if (hoistIdentity && f.identity) continue;
      if (f === proseField) continue;
      // The outcome renders in its own section, not as a chip — a whole sentence
      // in the inline property row would be unreadable, and it would say the same
      // thing twice.
      if (f === outcomeField) continue;
      // Same reason for read-only prose (#43855): a rejection reason is a
      // paragraph, and its labelled section below is where it can be read.
      if (isLockedProse(f)) continue;
      rows.push({
        ...edit.renderField(f, { chip: true }),
        icon: propertyGlyph(f.label, f.type),
      });
    }
    for (const v of o.vitals) {
      if (covered.has(v.label.trim().toLowerCase())) continue;
      rows.push({
        label: v.label,
        icon: propertyGlyph(v.label),
        value: v.value,
        hint: v.hint ?? undefined,
        intent: v.intent ?? undefined,
      });
    }
    return [...rows, ...completedField(o)];
  };

  /**
   * WHAT HAPPENED TO "Details". It was a third sidebar group holding Opened /
   * Updated / Completed, and Matt called it on the organization peek: "this is
   * repetitive". It was — twice over. `Opened` restates the `Created` row the
   * Activity timeline already shows (with an actor attached, which the Details
   * row never had), and `Updated` restates the newest event's timestamp, which
   * is the FIRST row of that same timeline. Two labeled rows to re-say the top
   * and bottom of the list beside them.
   *
   * So the section is gone and only the fact the timeline CANNOT carry stays:
   * `Completed`, which is a lifecycle stamp rather than an event. It folds into
   * Properties as one row, and only when it is set. `createdAt` survives as the
   * empty-state caption below — the one case where there is no timeline to read
   * it off.
   */
  const completedField = (o: ObjectPeekDto): PeekField[] =>
    o.fulfilledAt
      ? [
          {
            label: "Completed",
            icon: propertyGlyph("Completed"),
            value: formatDateTime(o.fulfilledAt),
          },
        ]
      : [];

  /**
   * Relationships collapse to one labeled row per group. A group whose label
   * already appears in Properties is DROPPED — the loader surfaces the owner
   * both as a resolved property and as an entity_links edge, and the frozen
   * grammar is one-fact-one-place: a peek never shows "Owner · Claude" twice
   * under two headings.
   */
  const relationshipFields = (
    o: ObjectPeekDto,
    shown: PeekField[],
  ): PeekField[] => {
    const claimed = new Set(
      shown.map((f) => f.label.trim().toLowerCase()),
    );
    return o.relationships.flatMap((g) => {
      const first = g.items[0];
      if (!first || claimed.has(g.label.trim().toLowerCase())) return [];
      return [
        {
          label: g.label,
          icon: propertyGlyph(g.label),
          value:
            g.items.length === 1
              ? first.label
              : `${first.label} +${g.items.length - 1}`,
          href: g.items.length === 1 ? (first.href ?? undefined) : undefined,
        },
      ];
    });
  };

  // Every property as one inline, editable chip.
  const properties = obj ? editableChips(obj) : [];
  const related = obj ? relationshipFields(obj, properties) : [];

  /**
   * The static bottom bar. The buttons render WHETHER OR NOT there is anything
   * to save — an auto-saving dialog with no button at all reads unfinished, and
   * "Done" is the gesture people reach for regardless (Matt, 2026-07-21: "we
   * always want the buttons even if it is auto saving"). The left slot carries
   * the one aggregate write-status line, which is what makes a silent
   * save-on-blur legible; per-field status still rides each row.
   */
  const footer = obj ? (
    <PeekDialogFooter
      leading={
        onRemove && obj.remove ? (
          <RemoveButton
            verb={obj.remove.verb}
            label={obj.remove.label}
            onRemove={() => onRemove(obj.kind, obj.uid)}
            onDone={onClose}
          />
        ) : null
      }
      status={edit?.renderSaveStatus?.() ?? null}
    >
      {/* `obj.actions` IS NOT RENDERED HERE AT ALL (Matt, 2026-07-22: "we still
          have 'open in work' or whatever buttons at the bottom of these modals —
          we don't need those and should get rid of them all").

          This started as a filter on the one key that misbehaved
          (`open-in-mode`), and a filter was the wrong shape: it left the footer
          claiming to be a navigation bar and made every future action a question
          of whether to add it to a denylist — which is how the styleguide came
          to keep painting "Open in Work" from a fixture whose key was `open`.
          The honest rule is about the SURFACE, not the key. Every action
          `objectActions` composes is navigational (open the kind's list, jump
          to another workspace surface); a peek is a place to READ AND EDIT the
          object in front of you, and it already has the one navigation it needs — the ⤢
          Expand that opens this object's own page. A row of links pointing
          somewhere else is exactly what a modal should not end with.

          They are not lost: `/o/<uid>` renders the same array in its Actions
          card (`object-resolver.tsx`), where a way back up to the list is a
          legitimate thing for a full page to offer. Kind slots still add real
          buttons through `footerExtras` — an action that DOES something to this
          object belongs here; a link away from it does not. */}
      {footerExtras?.(obj, refresh)}
      <Button size="sm" onClick={onClose}>
        Done
      </Button>
    </PeekDialogFooter>
  ) : null;

  // The identity descriptor (name/title) is hoisted into the masthead title slot
  // as an editable heading when a provider offers `renderTitle`; its write status
  // rides the DESCRIPTION slot, never the DialogTitle (which is the a11y name).
  const identityField =
    edit?.renderTitle && obj
      ? ((obj.fields ?? []).find((f) => f.identity) ?? null)
      : null;
  const titleNode = obj ? (
    identityField && edit?.renderTitle
      ? edit.renderTitle(identityField, obj.title)
      : obj.title
  ) : (
    <Skeleton className="h-6 w-72" />
  );
  const titleStatus =
    identityField && edit?.renderTitleStatus
      ? edit.renderTitleStatus(identityField)
      : undefined;

  return (
    <PeekDialog
      open
      onClose={onClose}
      // The kind bar: glyph AND noun (Matt, 2026-07-22: "we need the name of the
      // object - like task"). The glyph shows for every object; a caller that
      // overrides the eyebrow replaces only the TEXT, so a custom scope line
      // keeps the kind's identity glyph instead of trading it away.
      icon={obj ? <MastheadIcon kind={obj.kind} /> : undefined}
      eyebrow={
        !obj ? (
          <Skeleton className="h-3 w-16" />
        ) : eyebrow ? (
          eyebrow(obj)
        ) : (
          <ObjectKindLine obj={obj} />
        )
      }
      title={titleNode}
      description={titleStatus ?? undefined}
      expandHref={expandHref}
      expandLabel="Open full page"
      onExpand={onClose}
      footer={footer}
    >
      {error ? (
        <p className="type-body text-muted-foreground">
          Couldn&apos;t load this — it may have been deleted.
        </p>
      ) : null}

      {/* THE IDENTITY CLUSTER — description + properties held 8px apart, so they
          read as CONTINUATIONS OF THE TITLE rather than as two more stacked
          blocks (Matt, 2026-07-22, pointing at /styleguide/workflow-detail:
          "this is much more ideal"). Measured there: title →8px→ description
          →8px→ property row, then a large break before the next region. The peek
          had all three at the body's uniform 24px, which is why an object's own
          identity read as loosely related paragraphs instead of one masthead.

          The cluster is a real container, not three siblings with tuned margins:
          the body's `gap-6` then separates IT from the sections below, so "tight
          inside the identity, 24 between regions" survives any section being
          added, removed or reordered. ONE ORDER, EVERY KIND (Matt: "it should
          always go icon/type, then title, then description, then properties,
          then the rest") — which is why a goal's Outcome is no longer allowed to
          cut in here; it sits below with the other sections. */}
      <div className="flex flex-col gap-2">
        {/* DESCRIPTION — second, always, right under the title, and with NO label
            of its own (Matt, 2026-07-21: "description doesn't need a label"). It is
            prose under a heading; a "Description" caps mark over it was labeling
            the obvious. When the kind ships a prose descriptor the paragraph is
            EDITABLE IN PLACE and renders even when empty — an unset description
            that hides itself is unreachable. Without a descriptor it is the
            read-only paragraph it has always been.

            `essence` is still not eligible: it is the loader's composed one-liner
            ("Goal · 0 of 0 issues done"), so surfacing it here would claim prose
            that doesn't exist and restate vitals the properties already show. */}
        {obj && proseField && edit?.renderProse ? (
          <div className="type-body leading-body">
            {edit.renderProse(proseField, obj.description)}
          </div>
        ) : obj?.description ? (
          // Muted, matching the editable path — a kind without a prose descriptor
          // must not read a shade brighter than one that has it.
          <p className="type-body leading-body text-muted-foreground">
            {obj.description}
          </p>
        ) : null}

        {/* THE PROPERTIES — one inline row of chips, editable in place, NO label
            (Matt, 2026-07-21: "nor do we need that title"). A status chip opens a
            dropdown, a date chip a picker; there is no duplicate editable list. */}
        {obj ? (
          <PeekDialogPropertyChips fields={properties} />
        ) : (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        )}
      </div>

      {/* The commitment's finish line — for a goal, the single most important
          sentence on the object. It USED TO SIT ABOVE the description, which made
          goal the one kind that broke the fixed order; it is a labeled section
          like every other, so it belongs with them. Still first among the
          sections, so a goal's most important sentence is what follows its
          properties.

          EDITABLE when the kind ships an `outcome`-flagged descriptor (Matt,
          2026-07-22: "shouldn't we be able to edit the outcome of a goal?").
          Otherwise it stays the read-only paragraph, which is all a kind whose
          `targetOutcome` is derived rather than stored can honestly offer — an
          agent-action's, for instance, is its operation name, with no column
          behind it to write back to.

          The section renders whenever EITHER exists, so a goal with an empty
          outcome still shows the row with its placeholder — an unset editable
          field has to be reachable, or it can never be set. */}
      {obj && outcomeField && isLockedProse(outcomeField) ? (
        // A LOCKED outcome is the same defect as a locked description (#43855):
        // handing it to `renderProse` returns the fallback, so the declared value
        // would be replaced by `obj.targetOutcome`. Read-only means read its OWN
        // value.
        <PeekDialogSection title="Outcome">
          <ReadOnlyProse field={outcomeField} />
        </PeekDialogSection>
      ) : obj && outcomeField && edit?.renderProse ? (
        <PeekDialogSection title="Outcome">
          <div className="type-body leading-body">
            {edit.renderProse(outcomeField, obj.targetOutcome)}
          </div>
        </PeekDialogSection>
      ) : obj?.targetOutcome ? (
        <PeekDialogSection title="Outcome">
          <p className="type-body leading-body text-foreground">
            {obj.targetOutcome}
          </p>
        </PeekDialogSection>
      ) : null}

      {/* READ-ONLY PROSE — a locked `longtext` descriptor, one labelled section
          each (#43855). An application's rejection reason is the case that found
          this: the peek COMPELS the reason on write, so the one place it can be
          read back has to be a section a human can actually read — a paragraph,
          wrapped, under its own label, with the line saying why it cannot be
          edited. Sits after Outcome and before the kind extensions, so the fixed
          section order (outcome, then the kind's own prose, then extensions,
          then Related, then Activity) still reads top-to-bottom. */}
      {lockedProseFields.map((f) => (
        <PeekDialogSection key={f.key} title={f.label}>
          <ReadOnlyProse field={f} />
        </PeekDialogSection>
      ))}

      {/* Kind extensions (a task's commits / auto-verify, its goal link) stack
          in the one column now that Activity is no longer in a rail. */}
      {obj ? bodyExtension?.(obj, refresh) : null}
      {obj ? sidebarExtension?.(obj, refresh) : null}

      {related.length ? (
        <PeekDialogSection title="Related">
          <PeekDialogPropertyChips fields={related} />
        </PeekDialogSection>
      ) : null}

      {/* ACTIVITY — stacked with the other sections, not in a side rail (Matt,
          2026-07-21: "stack activity with the others"). History is last: it is
          the secondary read, and a single column reads top-to-bottom. */}
      {obj ? (
        <PeekDialogSection
          title="Activity"
          count={obj.eventTotal > events.length ? obj.eventTotal : undefined}
          action={
            expandHref && obj.eventTotal > events.length ? (
              <NextLink
                href={expandHref}
                onClick={onClose}
                className="type-subtext leading-ui text-muted-foreground hover:text-foreground hover:underline"
              >
                See all
              </NextLink>
            ) : null
          }
        >
          {events.length ? (
            <PeekDialogTimeline events={events} subjectTitle={obj.title} />
          ) : (
            <p className="type-subtext leading-body text-muted-foreground">
              No activity yet. Created {formatDateTime(obj.createdAt)}.
            </p>
          )}
        </PeekDialogSection>
      ) : null}
    </PeekDialog>
  );
}

/**
 * The peek's destructive control — one button, every archivable kind.
 *
 * Two-step by design: the first press arms it and the label becomes the
 * confirmation, so a mis-click beside the Done button cannot remove an object.
 * It sits in the footer's `leading` slot, as far from the primary as the bar
 * allows — the separation the task peek used to buy by keeping Archive out of
 * its property rail entirely.
 */
function RemoveButton({
  verb,
  label,
  onRemove,
  onDone,
}: {
  verb: "Delete" | "Archive";
  label: string;
  onRemove: () => Promise<unknown>;
  onDone: () => void;
}) {
  const [armed, setArmed] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  return (
    <Button
      variant={armed ? "destructive" : "ghost"}
      size="sm"
      disabled={pending}
      className={armed ? undefined : "text-muted-foreground hover:text-foreground"}
      onClick={() => {
        if (!armed) {
          setArmed(true);
          return;
        }
        setPending(true);
        void onRemove()
          .then(() => onDone())
          .finally(() => setPending(false));
      }}
      onBlur={() => setArmed(false)}
    >
      {pending ? "Removing…" : armed ? `${verb} this ${label}?` : verb}
    </Button>
  );
}

/**
 * Self-mounted object peek driven by the `?peek=<uid>` query param — the single
 * doorway `ObjectLink` / `useOpenObject` write to. Mount exactly ONCE, in the
 * root layout: the URL is the source of truth for which object is open, so any
 * surface can open one without holding state, Back closes it, and a pasted link
 * lands on it.
 *
 * The shell wires kind extensions in through the slots (they reach for server
 * actions, which can't live in this package) and computes `from` off the
 * current route, so Expand carries the origin mode.
 */
export function ObjectPeekHost({
  from,
  onRefresh,
  onRemove,
  ...slots
}: ObjectPeekSlots & {
  from?: string | null;
  onRefresh?: () => void;
  onRemove?: (kind: string, uid: string) => Promise<unknown>;
}) {
  const search = useSearchParams();
  const close = useCloseObject();
  const uid = search.get(PEEK_PARAM);
  return (
    <ObjectPeek
      uid={uid && uid.length ? uid : null}
      onClose={close}
      from={from}
      onRefresh={onRefresh}
      onRemove={onRemove}
      {...slots}
    />
  );
}
